import { useSyncExternalStore } from 'react';

export type QueueStatus = 'in-flight' | 'done' | 'failed';

export interface QueueEntry {
  id: string;
  conversationId: number;
  content: string;
  status: QueueStatus;
  // True once the user has opened the queue menu while this entry was visible.
  // Counter shows "done over total of unread entries"; read entries persist in
  // the list (so you can scroll to them) but stop adding to the badge total.
  read: boolean;
  // Optimistic message ID we inserted into the messages list (for scroll-into-view).
  optimisticMessageId: number;
  // Real server-side message ID, set once polling sees it (replaces optimistic).
  serverMessageId?: number;
  // For inspect-chat entries: which inspection item the AI created/updated
  // (the first one from chatResult.actions). Click → switch to Inspect tab
  // and scroll to [data-item-id]. Falls back to chat-message scrolling when
  // unset.
  targetItemId?: number;
  // For photo/video inspect uploads (api.aiInspect): the async batch id. The
  // items it creates are stamped with this batch_id; click → switch to Inspect
  // and scroll to [data-batch-id]. Used when targetItemId is unknown (the batch
  // worker creates items after enqueue, so there's no item_id at send time).
  batchId?: number;
  createdAt: number;
  finishedAt?: number;
}

// Hard cap on entries per conversation. When over, we drop the oldest READ +
// finished entries first so in-flight and unread state is never lost.
const MAX_ENTRIES_PER_CONVERSATION = 50;

// Stable empty array — useSyncExternalStore's getSnapshot requires referential
// equality when nothing has changed. Returning a fresh `[]` causes infinite loops.
const EMPTY: QueueEntry[] = Object.freeze([]) as unknown as QueueEntry[];

// Per-conversation in-memory queue. Survives across tab switches but not reload.
type State = Map<number, QueueEntry[]>;
let state: State = new Map();

const subs = new Set<() => void>();
const notify = () => subs.forEach((cb) => cb());

const subscribe = (cb: () => void): (() => void) => {
  subs.add(cb);
  return () => {
    subs.delete(cb);
  };
};

const getEntries = (conversationId: number): QueueEntry[] =>
  state.get(conversationId) ?? EMPTY;

function trim(list: QueueEntry[]): QueueEntry[] {
  if (list.length <= MAX_ENTRIES_PER_CONVERSATION) return list;
  const overflow = list.length - MAX_ENTRIES_PER_CONVERSATION;
  // Drop oldest READ + finished entries first; never drop in-flight or unread.
  const droppable: number[] = [];
  for (let i = 0; i < list.length && droppable.length < overflow; i++) {
    const e = list[i];
    if (e.read && (e.status === 'done' || e.status === 'failed')) droppable.push(i);
  }
  if (droppable.length === 0) return list;
  const drop = new Set(droppable);
  return list.filter((_, i) => !drop.has(i));
}

const setList = (conversationId: number, list: QueueEntry[]): void => {
  state = new Map(state).set(conversationId, trim(list));
};

export const messageQueue = {
  enqueue(entry: Omit<QueueEntry, 'read'>): void {
    const list = [...getEntries(entry.conversationId), { ...entry, read: false }];
    setList(entry.conversationId, list);
    notify();
  },

  setServerMessageId(conversationId: number, queueId: string, serverMessageId: number): void {
    const list = getEntries(conversationId);
    const idx = list.findIndex((e) => e.id === queueId);
    if (idx < 0 || list[idx].serverMessageId === serverMessageId) return;
    const next = [...list];
    next[idx] = { ...next[idx], serverMessageId };
    setList(conversationId, next);
    notify();
  },

  setTargetItem(conversationId: number, queueId: string, targetItemId: number): void {
    const list = getEntries(conversationId);
    const idx = list.findIndex((e) => e.id === queueId);
    if (idx < 0 || list[idx].targetItemId === targetItemId) return;
    const next = [...list];
    next[idx] = { ...next[idx], targetItemId };
    setList(conversationId, next);
    notify();
  },

  setBatch(conversationId: number, queueId: string, batchId: number): void {
    const list = getEntries(conversationId);
    const idx = list.findIndex((e) => e.id === queueId);
    if (idx < 0 || list[idx].batchId === batchId) return;
    const next = [...list];
    next[idx] = { ...next[idx], batchId };
    setList(conversationId, next);
    notify();
  },

  markOldestDone(conversationId: number, when: number): void {
    const list = getEntries(conversationId);
    const idx = list.findIndex((e) => e.status === 'in-flight');
    if (idx < 0) return;
    const next = [...list];
    next[idx] = { ...next[idx], status: 'done', finishedAt: when };
    setList(conversationId, next);
    notify();
  },

  fail(conversationId: number, queueId: string): void {
    const list = getEntries(conversationId);
    const idx = list.findIndex((e) => e.id === queueId);
    if (idx < 0) return;
    const next = [...list];
    next[idx] = { ...next[idx], status: 'failed', finishedAt: Date.now() };
    setList(conversationId, next);
    notify();
  },

  // Mark every current entry as read. Called when the user opens the badge menu.
  // No-op if there's nothing unread (prevents Menu.onOpen churn on empty state).
  markAllRead(conversationId: number): void {
    const list = getEntries(conversationId);
    if (list.length === 0 || list.every((e) => e.read)) return;
    const next = list.map((e) => (e.read ? e : { ...e, read: true }));
    setList(conversationId, next);
    notify();
  },

  clearConversation(conversationId: number): void {
    if (!state.has(conversationId)) return;
    state = new Map(state);
    state.delete(conversationId);
    notify();
  },
};

export function useMessageQueue(conversationId: number | undefined): QueueEntry[] {
  return useSyncExternalStore(
    subscribe,
    () => (conversationId ? getEntries(conversationId) : EMPTY),
    () => EMPTY,
  );
}

export interface QueueCounts {
  // "Total" / "done" reflect the unread window — entries the user hasn't seen
  // yet, so the badge can show e.g. "16/40" before open and "0/24" after.
  total: number;
  done: number;
  inFlight: number;
  failed: number;
  // Overall sizes (across all entries, read or not) — useful for the dropdown
  // header and disabling the send button when at the in-flight cap.
  allTotal: number;
  allInFlight: number;
}

export function selectCounts(entries: QueueEntry[]): QueueCounts {
  let total = 0;
  let done = 0;
  let inFlight = 0;
  let failed = 0;
  let allInFlight = 0;
  for (const e of entries) {
    if (e.status === 'in-flight') allInFlight += 1;
    if (e.read) continue;
    total += 1;
    if (e.status === 'done') done += 1;
    else if (e.status === 'in-flight') inFlight += 1;
    else if (e.status === 'failed') failed += 1;
  }
  return { total, done, inFlight, failed, allTotal: entries.length, allInFlight };
}
